import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users2, UserCog, Link as LinkIcon, X, ArrowLeftRight, UserX, UserCheck, AlertTriangle } from 'lucide-react';
import { lookupUsers, supervisorSetMemberRole, supervisorSetMemberStatus } from '../../services/api';
import {
  listAssignments,
  listTranscriberSupervisorAssignments,
  listTranscriberAssignments,
  supervisorCreateTranscriberAssignment,
  supervisorDeleteTranscriberAssignment,
} from '../../services/annotationApi';
import { useCurrentUser } from '../../context/UserContext';
import { UserEmail } from '../../components/common/UserEmail';

interface UserRow {
  userId: string;
  displayName: string;
  email: string;
  role: string;
  approvalStatus?: string;
  supervisorId?: string;
}

interface TranscriberAssignmentRow {
  assignmentId: string;
  enumeratorId: string;
  transcriberId: string;
}

export default function MyTeamAssignmentsPage() {
  const { userId: meId } = useCurrentUser();
  const qc = useQueryClient();

  // Direct enumerators (supervisor_assignments where supervisor_id = me).
  const { data: supAssignments = [] } = useQuery({
    queryKey: ['my-supervisor-assignments', meId],
    queryFn: () => listAssignments({ supervisorId: meId! }),
    enabled: !!meId,
  });

  // Transcribers under me (transcriber_supervisor_assignments where supervisor_id = me).
  const { data: tsupAssignments = [] } = useQuery({
    queryKey: ['my-transcriber-supervisor-assignments', meId],
    queryFn: () => listTranscriberSupervisorAssignments({ supervisorId: meId! }),
    enabled: !!meId,
  });

  // Transcriber→enumerator assignments across the supervisor's entire subtree,
  // fetched in ONE round-trip (backend joins via transcriber_supervisor_assignments).
  // Bucketed client-side by transcriberId so the per-column rendering stays
  // identical to what the old per-transcriber loop produced.
  const { data: allSubtreeAssignments = [] } = useQuery({
    queryKey: ['transcriber-assignments-subtree', meId],
    enabled: !!meId,
    queryFn: () => listTranscriberAssignments({ supervisorId: meId! }),
  });
  const assignmentsByTranscriber = useMemo(() => {
    const map: Record<string, TranscriberAssignmentRow[]> = {};
    for (const a of allSubtreeAssignments) {
      const arr = map[a.transcriberId];
      if (arr) arr.push(a); else map[a.transcriberId] = [a];
    }
    return map;
  }, [allSubtreeAssignments]);

  // Collect every userId referenced anywhere on this page and resolve them
  // in one bulk lookup. Using lookupUsers (instead of broad getUsers) because
  // the role-scoped GET /users filters out users whose user.supervisorId
  // doesn't equal the current viewer — which drops indirect enumerators
  // (registered under a different supervisor's code but assigned to a
  // transcriber under this supervisor) even though we have legitimate read
  // access to their names.
  const allReferencedIds = useMemo(() => {
    const s = new Set<string>();
    if (meId) s.add(meId);
    for (const a of supAssignments) s.add(a.enumeratorId);
    for (const a of tsupAssignments) s.add(a.transcriberId);
    for (const tid of Object.keys(assignmentsByTranscriber)) {
      for (const a of assignmentsByTranscriber[tid] ?? []) s.add(a.enumeratorId);
    }
    return [...s];
  }, [meId, supAssignments, tsupAssignments, assignmentsByTranscriber]);

  const { data: usersLookup } = useQuery({
    queryKey: ['myteam-users-lookup', allReferencedIds],
    enabled: allReferencedIds.length > 0,
    queryFn: () => lookupUsers(allReferencedIds),
  });
  const userMap = useMemo(() => {
    const m = new Map<string, UserRow>();
    for (const u of usersLookup?.users ?? []) {
      m.set(u.userId, {
        userId: u.userId,
        displayName: u.displayName,
        email: u.email,
        role: u.role,
        approvalStatus: u.approvalStatus,
        supervisorId: u.supervisorId,
      });
    }
    return m;
  }, [usersLookup]);

  const directEnumerators = useMemo(() => {
    const out: UserRow[] = [];
    for (const a of supAssignments) {
      const u = userMap.get(a.enumeratorId);
      if (u) out.push(u);
    }
    return out;
  }, [supAssignments, userMap]);

  const myTranscribers = useMemo(() => {
    const out: UserRow[] = [];
    for (const a of tsupAssignments) {
      const u = userMap.get(a.transcriberId);
      if (u) out.push(u);
    }
    return out;
  }, [tsupAssignments, userMap]);

  // Build the "all my enumerators" view: direct + indirect (via any of my
  // transcribers). Tag each with whether it's direct, or which transcriber(s)
  // make it indirect. Indirect-only enumerators are still considered "in my
  // team" per the supervisor's task-visibility predicate. We KEEP rows for
  // enumerators whose user record is missing (orphan IDs in
  // transcriber_assignments with no matching users entry) so the supervisor
  // can see and clean them up rather than silently filtering them out.
  type EnumeratorRow = {
    userId: string;
    displayName: string;
    email: string;
    role: string;
    approvalStatus?: string;
    supervisorId?: string;
    isUnknown: boolean;       // true if no users-table entry was found
    sourceDirect: boolean;
    viaTranscribers: string[]; // transcriber userIds linking to this enumerator
  };
  const allMyEnumerators: EnumeratorRow[] = useMemo(() => {
    const directIds = new Set(directEnumerators.map((u) => u.userId));
    const result = new Map<string, EnumeratorRow>();
    for (const u of directEnumerators) {
      result.set(u.userId, {
        userId: u.userId,
        displayName: u.displayName,
        email: u.email,
        role: u.role,
        approvalStatus: u.approvalStatus,
        supervisorId: u.supervisorId,
        isUnknown: false,
        sourceDirect: true,
        viaTranscribers: [],
      });
    }
    for (const tid of Object.keys(assignmentsByTranscriber)) {
      for (const a of assignmentsByTranscriber[tid] ?? []) {
        const eId = a.enumeratorId;
        const u = userMap.get(eId);
        const existing = result.get(eId);
        if (existing) {
          if (!existing.viaTranscribers.includes(tid)) existing.viaTranscribers.push(tid);
        } else {
          result.set(eId, {
            userId: eId,
            displayName: u?.displayName ?? `Unknown (${eId.slice(0, 8)})`,
            email: u?.email ?? '',
            role: u?.role ?? 'enumerator',
            approvalStatus: u?.approvalStatus,
            supervisorId: u?.supervisorId,
            isUnknown: !u,
            sourceDirect: directIds.has(eId),
            viaTranscribers: [tid],
          });
        }
      }
    }
    return [...result.values()];
  }, [directEnumerators, assignmentsByTranscriber, userMap]);

  // Per-enumerator list of transcribers assigned. Derived from the same map.
  const transcribersByEnumerator = useMemo(() => {
    const m: Record<string, TranscriberAssignmentRow[]> = {};
    for (const tid of Object.keys(assignmentsByTranscriber)) {
      for (const a of assignmentsByTranscriber[tid] ?? []) {
        if (!m[a.enumeratorId]) m[a.enumeratorId] = [];
        m[a.enumeratorId].push(a);
      }
    }
    return m;
  }, [assignmentsByTranscriber]);

  const createMutation = useMutation({
    mutationFn: (data: { enumeratorId: string; transcriberId: string }) =>
      supervisorCreateTranscriberAssignment(data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['transcriber-assignments-by-transcriber'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (assignmentId: string) => supervisorDeleteTranscriberAssignment(assignmentId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['transcriber-assignments-by-transcriber'] }),
  });

  // ── Member management: role flip (enumerator ⇄ transcriber) + disable/enable ─
  const invalidateTeam = () => {
    qc.invalidateQueries({ queryKey: ['my-supervisor-assignments', meId] });
    qc.invalidateQueries({ queryKey: ['my-transcriber-supervisor-assignments', meId] });
    qc.invalidateQueries({ queryKey: ['transcriber-assignments-subtree', meId] });
    qc.invalidateQueries({ queryKey: ['myteam-users-lookup'] });
  };

  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const flashToast = (type: 'success' | 'error', message: string, ms = 4000) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), ms);
  };

  type ConfirmAction =
    | { kind: 'role'; userId: string; name: string; currentRole: 'enumerator' | 'transcriber' }
    | { kind: 'disable'; userId: string; name: string }
    | { kind: 'enable'; userId: string; name: string };
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'enumerator' | 'transcriber' }) =>
      supervisorSetMemberRole(userId, role),
    onSuccess: (_data, vars) => {
      invalidateTeam();
      setConfirmAction(null);
      flashToast('success', `Role changed to ${vars.role}. They will be signed out and must log in again.`);
    },
    onError: (err: Error) => { setConfirmAction(null); flashToast('error', err.message, 6000); },
  });

  const statusMutation = useMutation({
    mutationFn: ({ userId, action }: { userId: string; action: 'disable' | 'enable' }) =>
      supervisorSetMemberStatus(userId, action),
    onSuccess: (_data, vars) => {
      invalidateTeam();
      setConfirmAction(null);
      flashToast('success', vars.action === 'disable' ? 'Member disabled.' : 'Member re-enabled.');
    },
    onError: (err: Error) => { setConfirmAction(null); flashToast('error', err.message, 6000); },
  });

  const busy = roleMutation.isPending || statusMutation.isPending;

  const runConfirm = () => {
    if (!confirmAction) return;
    if (confirmAction.kind === 'role') {
      roleMutation.mutate({
        userId: confirmAction.userId,
        role: confirmAction.currentRole === 'enumerator' ? 'transcriber' : 'enumerator',
      });
    } else {
      statusMutation.mutate({ userId: confirmAction.userId, action: confirmAction.kind });
    }
  };

  // A member is the supervisor's to manage only if they registered under this
  // supervisor's code (supervisorId === me) and their user record resolved.
  // Indirect/orphan enumerators are excluded — the backend enforces the same.
  const canManage = (m: { supervisorId?: string; isUnknown?: boolean }) =>
    !!meId && !m.isUnknown && m.supervisorId === meId;

  // One pair of picker states, keyed by the "host" id so only one picker is
  // ever open at a time across either column.
  const [enumPickerOpen, setEnumPickerOpen] = useState<string | null>(null);
  const [enumPick, setEnumPick] = useState('');
  const [transPickerOpen, setTransPickerOpen] = useState<string | null>(null);
  const [transPick, setTransPick] = useState('');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Team</h1>
        <p className="text-gray-500 text-sm mt-1">
          Enumerators and transcribers in your supervisory subtree. Edit assignments from either side, switch a member between enumerator and transcriber, or disable someone registered under you.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ────────────── Enumerators column ────────────── */}
        <div className="card p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
            <Users2 size={16} className="text-violet-600" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              Enumerators ({allMyEnumerators.length})
            </h2>
          </div>
          <ul className="divide-y divide-gray-100 max-h-[32rem] overflow-y-auto">
            {allMyEnumerators.length === 0 ? (
              <li className="px-4 py-6 text-sm text-gray-400 text-center">No enumerators yet.</li>
            ) : (
              allMyEnumerators.map((e) => {
                const assignments = transcribersByEnumerator[e.userId] ?? [];
                const isOpen = enumPickerOpen === e.userId;
                return (
                  <li key={e.userId} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{e.displayName}</p>
                        {e.email && <UserEmail email={e.email} role={e.role} />}
                        <div className="mt-1 flex flex-wrap gap-1">
                          {e.sourceDirect ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
                              Direct
                            </span>
                          ) : (
                            e.viaTranscribers.map((tid) => {
                              const t = userMap.get(tid);
                              return (
                                <span
                                  key={tid}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-700"
                                >
                                  Via {t?.displayName ?? tid.slice(0, 8)}
                                </span>
                              );
                            })
                          )}
                          {e.isUnknown && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-700">
                              Orphan — user record missing
                            </span>
                          )}
                          {!e.isUnknown && e.approvalStatus && e.approvalStatus !== 'approved' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                              {e.approvalStatus}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 flex flex-wrap items-center justify-end gap-1">
                        {canManage(e) && (
                          <>
                            <button
                              onClick={() => setConfirmAction({ kind: 'role', userId: e.userId, name: e.displayName, currentRole: 'enumerator' })}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-sky-50 text-sky-700 hover:bg-sky-100"
                              title="Switch this member to Transcriber"
                            >
                              <ArrowLeftRight size={12} />
                              Make Transcriber
                            </button>
                            {e.approvalStatus === 'disabled' ? (
                              <button
                                onClick={() => setConfirmAction({ kind: 'enable', userId: e.userId, name: e.displayName })}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                title="Re-enable this member"
                              >
                                <UserCheck size={12} />
                                Enable
                              </button>
                            ) : (
                              <button
                                onClick={() => setConfirmAction({ kind: 'disable', userId: e.userId, name: e.displayName })}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100"
                                title="Disable (revoke access)"
                              >
                                <UserX size={12} />
                                Disable
                              </button>
                            )}
                          </>
                        )}
                        <button
                          onClick={() => {
                            setEnumPickerOpen(isOpen ? null : e.userId);
                            setEnumPick('');
                            setTransPickerOpen(null);
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-violet-50 text-violet-700 hover:bg-violet-100"
                        >
                          <LinkIcon size={12} />
                          Assign Transcriber
                        </button>
                      </div>
                    </div>
                    {assignments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {assignments.map((a) => {
                          const t = userMap.get(a.transcriberId);
                          return (
                            <span
                              key={a.assignmentId}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-50 text-sky-800 text-xs"
                            >
                              {t?.displayName ?? a.transcriberId.slice(0, 8)}
                              <button
                                onClick={() => deleteMutation.mutate(a.assignmentId)}
                                className="text-sky-600 hover:text-red-600"
                                title="Remove assignment"
                              >
                                <X size={12} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {isOpen && (
                      <div className="mt-2 flex items-center gap-2">
                        <select
                          className="input flex-1"
                          value={enumPick}
                          onChange={(ev) => setEnumPick(ev.target.value)}
                        >
                          <option value="">Choose a transcriber…</option>
                          {myTranscribers
                            .filter((t) => !assignments.some((a) => a.transcriberId === t.userId))
                            .map((t) => (
                              <option key={t.userId} value={t.userId}>
                                {t.displayName} ({t.email})
                              </option>
                            ))}
                        </select>
                        <button
                          className="btn-primary"
                          disabled={!enumPick || createMutation.isPending}
                          onClick={() => {
                            createMutation.mutate({
                              enumeratorId: e.userId,
                              transcriberId: enumPick,
                            });
                            setEnumPickerOpen(null);
                          }}
                        >
                          Assign
                        </button>
                      </div>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>

        {/* ────────────── Transcribers column ────────────── */}
        <div className="card p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
            <UserCog size={16} className="text-sky-600" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              Transcribers ({myTranscribers.length})
            </h2>
          </div>
          <ul className="divide-y divide-gray-100 max-h-[32rem] overflow-y-auto">
            {myTranscribers.length === 0 ? (
              <li className="px-4 py-6 text-sm text-gray-400 text-center">No transcribers yet.</li>
            ) : (
              myTranscribers.map((t) => {
                const assignments = assignmentsByTranscriber[t.userId] ?? [];
                const isOpen = transPickerOpen === t.userId;
                const assignedEnumIds = new Set(assignments.map((a) => a.enumeratorId));
                return (
                  <li key={t.userId} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{t.displayName}</p>
                        <UserEmail email={t.email} role={t.role} />
                        {t.approvalStatus && t.approvalStatus !== 'approved' && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                              {t.approvalStatus}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 flex flex-wrap items-center justify-end gap-1">
                        {canManage(t) && (
                          <>
                            <button
                              onClick={() => setConfirmAction({ kind: 'role', userId: t.userId, name: t.displayName, currentRole: 'transcriber' })}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-violet-50 text-violet-700 hover:bg-violet-100"
                              title="Switch this member to Enumerator"
                            >
                              <ArrowLeftRight size={12} />
                              Make Enumerator
                            </button>
                            {t.approvalStatus === 'disabled' ? (
                              <button
                                onClick={() => setConfirmAction({ kind: 'enable', userId: t.userId, name: t.displayName })}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                title="Re-enable this member"
                              >
                                <UserCheck size={12} />
                                Enable
                              </button>
                            ) : (
                              <button
                                onClick={() => setConfirmAction({ kind: 'disable', userId: t.userId, name: t.displayName })}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100"
                                title="Disable (revoke access)"
                              >
                                <UserX size={12} />
                                Disable
                              </button>
                            )}
                          </>
                        )}
                        <button
                          onClick={() => {
                            setTransPickerOpen(isOpen ? null : t.userId);
                            setTransPick('');
                            setEnumPickerOpen(null);
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-sky-50 text-sky-700 hover:bg-sky-100"
                        >
                          <LinkIcon size={12} />
                          Assign Enumerator
                        </button>
                      </div>
                    </div>
                    {assignments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {assignments.map((a) => {
                          const e = userMap.get(a.enumeratorId);
                          return (
                            <span
                              key={a.assignmentId}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-50 text-violet-800 text-xs"
                            >
                              {e?.displayName ?? a.enumeratorId.slice(0, 8)}
                              <button
                                onClick={() => deleteMutation.mutate(a.assignmentId)}
                                className="text-violet-600 hover:text-red-600"
                                title="Remove assignment"
                              >
                                <X size={12} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {isOpen && (
                      <div className="mt-2 flex items-center gap-2">
                        <select
                          className="input flex-1"
                          value={transPick}
                          onChange={(ev) => setTransPick(ev.target.value)}
                        >
                          <option value="">Choose an enumerator…</option>
                          {allMyEnumerators
                            .filter((e) => !assignedEnumIds.has(e.userId))
                            .map((e) => (
                              <option key={e.userId} value={e.userId}>
                                {e.displayName} ({e.email})
                              </option>
                            ))}
                        </select>
                        <button
                          className="btn-primary"
                          disabled={!transPick || createMutation.isPending}
                          onClick={() => {
                            createMutation.mutate({
                              enumeratorId: transPick,
                              transcriberId: t.userId,
                            });
                            setTransPickerOpen(null);
                          }}
                        >
                          Assign
                        </button>
                      </div>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      </div>

      {/* Confirm dialog for role change / disable / enable */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">
                  {confirmAction.kind === 'role'
                    ? `Change ${confirmAction.name}'s role?`
                    : confirmAction.kind === 'disable'
                    ? `Disable ${confirmAction.name}?`
                    : `Re-enable ${confirmAction.name}?`}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {confirmAction.kind === 'role'
                    ? `They will become ${confirmAction.currentRole === 'enumerator' ? 'a Transcriber' : 'an Enumerator'}. Their existing assignment links will be reset, and they will be signed out and must log in again.`
                    : confirmAction.kind === 'disable'
                    ? 'They will lose access immediately and be signed out. You can re-enable them here later.'
                    : 'They will regain access with their previous role.'}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                onClick={() => setConfirmAction(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                className={`px-3 py-1.5 rounded-md text-sm font-medium text-white disabled:opacity-50 ${
                  confirmAction.kind === 'disable' ? 'bg-red-600 hover:bg-red-700' : 'bg-violet-600 hover:bg-violet-700'
                }`}
                onClick={runConfirm}
                disabled={busy}
              >
                {busy
                  ? 'Working…'
                  : confirmAction.kind === 'role'
                  ? 'Change role'
                  : confirmAction.kind === 'disable'
                  ? 'Disable'
                  : 'Enable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium max-w-md ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
